export const CONSTANTS = {
  BASE_URL: import.meta.env.BASE_URL,
  APP_ENV: import.meta.env.VITE_APP_ENV,
  APP_SLUG: import.meta.env.VITE_APP_SLUG,
  IS_PRODUCTION: import.meta.env.VITE_APP_ENV === "production",
  IS_STAGING: import.meta.env.VITE_APP_ENV === "staging",
  IS_LOCAL: import.meta.env.VITE_APP_ENV === "local",
  // Flip to true once the backend subscription migration is live on prod.
  SELF_SERVE_SUBSCRIPTION: true,
  APP_TITLE: import.meta.env.VITE_APP_TITLE,
  BACKEND_URL: import.meta.env.VITE_BACKEND_API,
  CLERK_PUBLISHABLE_KEY: import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
  DEFAULT_FOOTER_HEIGHT: 220,
  PUSHER_KEY: import.meta.env.VITE_PUSHER_KEY,
  // Unset = web push off; the tab falls back to raising OS notifications itself.
  ONESIGNAL_APP_ID: import.meta.env.VITE_ONESIGNAL_APP_ID,
  LOGO_FULL: import.meta.env.VITE_LOGO_FULL,
  LOGO_FULL_LIGHT: import.meta.env.VITE_LOGO_FULL_LIGHT,
  LOGO_ICON: import.meta.env.VITE_LOGO_ICON,
  LOGO_ICON_DARK: import.meta.env.VITE_LOGO_ICON_DARK,
  FAVICON: import.meta.env.VITE_FAVICON,
  BRAND_NAME: import.meta.env.VITE_BRAND_NAME,
  SUPPORT_EMAIL: import.meta.env.VITE_SUPPORT_EMAIL,
  SALES_PHONE: "+9779817198789",
  COMPANY_ADDRESS: "",
  COMPANY_TAX_ID: "",
  TERMS_URL: import.meta.env.VITE_TERMS_URL,
  PRIVACY_URL: import.meta.env.VITE_PRIVACY_URL,
  // Project API key: write-only and public by design (it ships in the browser
  // bundle), same as the Sentry DSN in instrumentation.ts. The env var stays as
  // an override so a dev project can be pointed at without a code change.
  POSTHOG_KEY:
    import.meta.env.VITE_POSTHOG_KEY ??
    "phc_tmyvLJ3xg96equ6vhRCNdJjXLXMfA8P2x8d2NrnGoajD",
  POSTHOG_HOST: import.meta.env.VITE_POSTHOG_HOST ?? "https://eu.i.posthog.com",
  FACEBOOK_APP_ID: import.meta.env.VITE_FACEBOOK_APP_ID,
  // Public Meta config ids, not secrets - env overrides for other Meta apps.
  WHATSAPP_APP_ID: import.meta.env.VITE_WHATSAPP_APP_ID ?? "6810592509033085",
  WHATSAPP_CLOUD_CONFIG_ID:
    import.meta.env.VITE_WHATSAPP_CLOUD_CONFIG_ID ?? "2437639223097720",
  INSTAGRAM_CLIENT_ID: import.meta.env.VITE_INSTAGRAM_CLIENT_ID,
  FACEBOOK_ADS_CONFIG_ID: import.meta.env
    .VITE_FACEBOOK_LOGIN_ADS_CONFIGURATION_ID,
  MESSENGER_SCOPES:
    "pages_manage_metadata,pages_messaging,pages_read_engagement,pages_show_list,business_management,pages_read_user_content,pages_manage_engagement",
};

// The single rebrand knob: name, @-command keyword, muted copy, and studio
// routes all derive from this one value.
const RAW_ASSISTANT_NAME = "Echo";
// Never shown all-lowercase: keep any intentional casing, else Title-Case it.
const AI_ASSISTANT_NAME = /[A-Z]/.test(RAW_ASSISTANT_NAME)
  ? RAW_ASSISTANT_NAME
  : RAW_ASSISTANT_NAME.replace(/\b\w/g, (char) => char.toUpperCase());
const AI_ASSISTANT_SLUG = AI_ASSISTANT_NAME.trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/(^-|-$)/g, "");

/**
 * One repo ships two brands off VITE_APP_SLUG: ClinicPilot for clinics, Replyn
 * for general business. Anything clinic-specific branches on this rather than
 * on a loose slug comparison.
 */
export const IS_CLINIC_APP = CONSTANTS.APP_SLUG === "clinicpilot";

/** Who the workspace talks to and what it is, in each brand's own words. */
export const AUDIENCE = IS_CLINIC_APP ? "patients" : "customers";
export const AUDIENCE_TITLE = IS_CLINIC_APP ? "Patients" : "Customers";
export const AUDIENCE_NOUN = IS_CLINIC_APP ? "patient" : "customer";
export const BUSINESS_NOUN = IS_CLINIC_APP ? "clinic" : "business";

/** Echo's mark. An object, not a string, so handing it to Iconify's `Icon`
 *  is a type error: only `AppIcon` knows how to draw it. */
export const ECHO_MARK = { mark: "echo" } as const;
export type EchoMark = typeof ECHO_MARK;

export const AI_ASSISTANT = {
  name: AI_ASSISTANT_NAME,
  /** Product-facing label, e.g. the sidebar row and the auth hero: "Echo AI". */
  label: `${AI_ASSISTANT_NAME} AI`,
  /** The agent's mark, shared by every surface that attributes work to it.
   *  Render it with `AppIcon`. */
  icon: ECHO_MARK as EchoMark,
  /** Route base segment for the AI studio. */
  slug: AI_ASSISTANT_SLUG,
} as const;

/** Builds an absolute studio path from the slug, e.g. `assistantPath("runs/42")`. */
export const assistantPath = (sub = ""): string =>
  `/${AI_ASSISTANT.slug}${sub ? `/${sub}` : ""}`;

export const HELP_URL = "https://docs.replyn.io";

export const EXTERNAL_LINKS = {
  tags: {
    arcade: "https://app.arcade.software/share/ji4WOywimdoIgET8UA1L",
    blogs: [] as string[],
  },
  closingNotes: {
    arcade: "https://app.arcade.software/share/Lt0jRGz1pVg3RtOdUbTw",
    blogs: [] as string[],
  },
  adsManager: {
    youtube: "https://youtu.be/7BlTbX8VFIQ",
    blogs: [] as string[],
  },
  instagram: {
    connect:
      "https://www.youtube.com/watch?v=Bf9Sb2OHn9E&list=PLKDN-PEJ6uZs&index=1",
    reconnect:
      "https://www.youtube.com/watch?v=AMXny-QJXSM&list=PLKDN-PEJ6uZs&index=3",
    blogs: [] as string[],
  },
  snippets: {
    arcade: "https://app.arcade.software/share/aPDiFZ8qmkTQylluoow0",
    blogs: [] as string[],
  },
  savedFiles: {
    arcade: "https://app.arcade.software/share/aPDiFZ8qmkTQylluoow0",
    blogs: [] as string[],
  },
  wrapUp: {
    arcade: "https://app.arcade.software/share/dbRCvTJkaGeETHNMvM3N",
    blogs: [] as string[],
  },
  lifecycle: {
    arcade: "https://app.arcade.software/share/L3B2ui3FBnPEZnH5u1MU",
    blogs: [] as string[],
  },
  customViews: {
    arcade: "https://app.arcade.software/share/pyjdknVLmLcGkLzc8BPu",
    blogs: [] as string[],
  },
};

export const PUSHER_EVENTS = {
  // Every row the notification centre stores, on one event. The per-type events
  // below stay for their data-sync side effects - none of them notify.
  NOTIFICATION: "notification",
  NEW_CONTACT: "new-contact",
  NEW_ACTIVITY: "new-activity",
  NEW_MESSAGE: "new-message",
  MESSAGE_STATUS_UPDATE: "message-status-update",
  CONTACT_INTERACTION_UPDATE: "contact-interaction-update",
  TEMPLATE_UPDATE: "template-update",
  CONTACT_CHANNEL_UPDATE: "contact-channel-update",
  APPOINTMENT_CREATED: "appointment-created",
  APPOINTMENT_UPDATED: "appointment-updated",
  APPOINTMENT_DELETED: "appointment-deleted",
  MESSAGE_EDIT: "message-edit",
  MESSAGE_REACTION: "message-reaction",
  MESSAGE_MEDIA_READY: "message-media-ready",
  MESSAGE_RUN_FLAGS: "message-run-flags",
  MESSAGE_DELETE: "message-delete",
  MESSAGES_SYNCED: "messages-synced",
  ACTIVITY_UPDATE: "activity-update",
  CONTACT_UPDATE: "contact-update",
  CONTACT_HIDDEN: "contact-hidden",
  SEGMENT_EXPORT_READY: "segment-export-ready",
  MENTION_ALERT: "mention-alert",
  UNREAD_MENTION_UPDATE: "unread-mention-update",
  FOLLOW_UP_DUE_ALERT: "follow-up-due-alert",
  SLA_ALERT: "sla-alert",
  AGENT_STEP: "agent-step",
  AI_RUN_PHASE: "ai-run-phase",
  AI_MEDIA_READ: "ai-media-read",
  AI_SESSION: "ai-session",
  AI_SUGGESTION: "ai-suggestion",
  AI_ATTENTION: "ai-attention",
  APPOINTMENT_OUTCOME_REQUIRED: "appointment-outcome-required",
  APPOINTMENT_CRM_PUSH_FAILED: "appointment-crm-push-failed",
  PRESENCE_UPDATED: "presence-updated",
  ACCESS_CHANGED: "access-changed",
};

/**
 * Dev tools - the tweaks panel and the overrides it drives - ship to local and
 * beta only. An explicit allow-list rather than `!IS_PRODUCTION`, so a build
 * with VITE_APP_ENV unset can never leak them to real users. Read at call time
 * (not off CONSTANTS) so tests can stub the env.
 */
export const isDevToolsEnabled = (): boolean =>
  import.meta.env.VITE_APP_ENV === "local" ||
  import.meta.env.VITE_APP_ENV === "staging";

/**
 * In-app tutorials go to QA on beta first: same allow-list as the dev tools
 * until they are signed off, then this becomes `() => true`.
 */
export const isTutorialsEnabled = (): boolean => isDevToolsEnabled();
